/**
 * TanStack Query hooks against `/v1/agent/*` Hono RPC routes.
 *
 * Spec #149 Phase 3b (port-agent): replaces the apps/web `useAgent` /
 * `fetch('/api/agents/...')` server-state code paths.
 *
 * Conventions:
 *  - `agentKeys` is the single source of truth for query keys; mutations
 *    invalidate against a known scope so React Query stays in sync.
 *  - All hooks return raw `useQuery` / `useMutation` results — composition
 *    (loading / error rendering) is up to the consuming component.
 *  - The Hono RPC client (`getApi()`) attaches the bearer token at
 *    request-time via the headers callback in `lib/api.ts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { getApi } from '@/lib/api';

export const agentKeys = {
  all: ['agent'] as const,
  list: () => [...agentKeys.all, 'list'] as const,
  detail: (id: string) => [...agentKeys.all, 'detail', id] as const,
  memories: (id: string, category?: string) =>
    [...agentKeys.all, 'memories', id, category ?? 'all'] as const,
  repositories: (id: string) => [...agentKeys.all, 'repositories', id] as const,
  mailAccounts: (id: string) => [...agentKeys.all, 'mail-accounts', id] as const,
};

/** Query keys for the per-user GitHub integration (token + repo browse). */
export const githubKeys = {
  all: ['github'] as const,
  token: () => [...githubKeys.all, 'token'] as const,
  repos: () => [...githubKeys.all, 'repos'] as const,
};

async function unwrap<T>(res: { ok: boolean; status: number; json: () => Promise<T> }): Promise<T> {
  if (!res.ok) {
    const err = new Error(`api_error_${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ── Types ─────────────────────────────────────────────── */

export interface AgentListItem {
  id: string;
  name: string;
  llmProvider: string;
  llmModel: string;
  tools: string[];
  /** Spec #169: per-Agent tool selection (normalized by the API). */
  enabledTools: string[];
  extendedConfig: Record<string, unknown> | null;
}

interface AgentDetail {
  id: string;
  ownerId: string;
  name: string;
  llmProvider: string;
  llmModel: string;
  tools: string[];
  /** Spec #169: per-Agent tool selection (normalized by the API). */
  enabledTools: string[];
  extendedConfig: Record<string, unknown> | null;
  status: string | null;
  permissionLevel: 'watch' | 'touch' | 'act' | null;
}

interface AgentMemoryItem {
  id: string;
  agentId: string;
  key: string;
  content: string;
  category: string;
  sessionId: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ── List / Detail ─────────────────────────────────────── */

export function useAgents(): UseQueryResult<{ count: number; agents: AgentListItem[] }> {
  return useQuery({
    queryKey: agentKeys.list(),
    queryFn: async () => {
      const res = await getApi().v1.agent.$get();
      return unwrap(res) as Promise<{ count: number; agents: AgentListItem[] }>;
    },
  });
}

export function useAgent(agentId: string | null) {
  return useQuery({
    queryKey: agentId ? agentKeys.detail(agentId) : ['agent', 'detail', 'none'],
    enabled: !!agentId,
    queryFn: async () => {
      const res = await getApi().v1.agent[':id'].$get({ param: { id: agentId! } });
      return unwrap<AgentDetail>(res);
    },
  });
}

interface CreateAgentInput {
  name: string;
  llmProvider: string;
  llmModel: string;
  tools?: string[];
  directionId?: string;
  extendedConfig?: Record<string, unknown>;
  /** Spec #169: per-Agent tool selection. Omitting it = all tools enabled. */
  enabledTools?: string[];
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAgentInput) => {
      const res = await getApi().v1.agent.$post({
        json: { ...input, tools: input.tools ?? [] },
      });
      // POST / returns the agent core, so we widen to a partial.
      return unwrap<{ id: string; name: string; llmProvider: string; llmModel: string; tools: string[] }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.list() });
    },
  });
}

interface UpdateAgentInput {
  name?: string;
  llmProvider?: string;
  llmModel?: string;
  tools?: string[];
  extendedConfig?: Record<string, unknown>;
  /** Spec #169: an explicit array replaces the tool selection. */
  enabledTools?: string[];
}

export function useUpdateAgent(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateAgentInput) => {
      const res = await getApi().v1.agent[':id'].$patch({
        param: { id: agentId },
        json: input,
      });
      // PATCH /:id returns the agent core — see route impl.
      return unwrap<{ id: string; name: string; llmProvider: string; llmModel: string; tools: string[] }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
      void qc.invalidateQueries({ queryKey: agentKeys.list() });
    },
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await getApi().v1.agent[':id'].$delete({ param: { id: agentId } });
      return unwrap(res);
    },
    onSuccess: (_data, agentId) => {
      void qc.invalidateQueries({ queryKey: agentKeys.list() });
      qc.removeQueries({ queryKey: agentKeys.detail(agentId) });
    },
  });
}

/* ── Memories ──────────────────────────────────────────── */

export function useAgentMemories(agentId: string | null, category?: string) {
  return useQuery({
    queryKey: agentId ? agentKeys.memories(agentId, category) : ['agent', 'memories', 'none'],
    enabled: !!agentId,
    queryFn: async () => {
      const res = await getApi().v1.agent[':id'].memories.$get({
        param: { id: agentId! },
        query: category ? { category } : {},
      });
      return unwrap<{ count: number; memories: AgentMemoryItem[] }>(res);
    },
  });
}

export function useDeleteAgentMemory(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (memoryId: string) => {
      const res = await getApi().v1.agent[':id'].memories[':memoryId'].$delete({
        param: { id: agentId, memoryId },
      });
      return unwrap<{ deleted: boolean }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...agentKeys.all, 'memories', agentId] });
    },
  });
}

/* ── Work repositories (agent_repositories, Spec #126) ───── */

export type AgentRepoWorkflowMode = 'auto' | 'direct_push' | 'pr_only';

export interface AgentRepository {
  id: string;
  githubUrl: string;
  githubFullName: string | null;
  defaultBranch: string;
  workflowMode: AgentRepoWorkflowMode;
  createdAt: string;
}

export function useAgentRepositories(agentId: string | null) {
  return useQuery({
    queryKey: agentId
      ? agentKeys.repositories(agentId)
      : ['agent', 'repositories', 'none'],
    enabled: !!agentId,
    queryFn: async () => {
      const res = await getApi().v1.agent[':id'].repositories.$get({
        param: { id: agentId! },
      });
      return unwrap<{ count: number; repositories: AgentRepository[] }>(res);
    },
  });
}

interface AddAgentRepositoryInput {
  githubFullName: string;
  defaultBranch?: string;
  workflowMode?: AgentRepoWorkflowMode;
}

export function useAddAgentRepository(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddAgentRepositoryInput) => {
      const res = await getApi().v1.agent[':id'].repositories.$post({
        param: { id: agentId },
        json: {
          githubFullName: input.githubFullName,
          defaultBranch: input.defaultBranch ?? 'main',
          workflowMode: input.workflowMode ?? 'auto',
        },
      });
      return unwrap<AgentRepository>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.repositories(agentId) });
    },
  });
}

export function useRemoveAgentRepository(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (linkId: string) => {
      const res = await getApi().v1.agent[':id'].repositories[':linkId'].$delete(
        { param: { id: agentId, linkId } },
      );
      return unwrap<{ deleted: boolean }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.repositories(agentId) });
    },
  });
}

/* ── Mail accounts (agent_mail_accounts, Spec #42) ───────── */

export interface MailServerConfigInput {
  host: string;
  port: number;
  userName: string;
  password: string;
  useSsl: boolean;
  startSsl: boolean;
  verifySsl: boolean;
}

interface MailServerConfigMasked {
  host: string;
  port: number;
  userName: string;
  useSsl: boolean;
  startSsl: boolean;
  verifySsl: boolean;
}

export interface AgentMailAccount {
  accountName: string;
  fullName: string;
  emailAddress: string;
  incoming: MailServerConfigMasked;
  outgoing: MailServerConfigMasked | null;
}

export function useAgentMailAccounts(agentId: string | null) {
  return useQuery({
    queryKey: agentId
      ? agentKeys.mailAccounts(agentId)
      : ['agent', 'mail-accounts', 'none'],
    enabled: !!agentId,
    queryFn: async () => {
      const res = await getApi().v1.agent[':id']['mail-accounts'].$get({
        param: { id: agentId! },
      });
      return unwrap<{ count: number; accounts: AgentMailAccount[] }>(res);
    },
  });
}

interface SaveAgentMailAccountInput {
  accountName: string;
  fullName: string;
  emailAddress: string;
  incoming: MailServerConfigInput;
  outgoing?: MailServerConfigInput;
}

export function useSaveAgentMailAccount(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveAgentMailAccountInput) => {
      const res = await getApi().v1.agent[':id']['mail-accounts'].$put({
        param: { id: agentId },
        json: input,
      });
      return unwrap<AgentMailAccount>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.mailAccounts(agentId) });
    },
  });
}

export function useDeleteAgentMailAccount(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountName: string) => {
      const res = await getApi().v1.agent[':id']['mail-accounts'][':accountName'].$delete(
        { param: { id: agentId, accountName } },
      );
      return unwrap<{ deleted: boolean }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentKeys.mailAccounts(agentId) });
    },
  });
}

/* ── GitHub integration (per-user token + repo browse) ───── */

/** How the stored token was obtained — drives the "OAuth 接続済み / PAT 登録済み" label. */
type GithubTokenSource = 'oauth' | 'pat';

export interface GithubTokenStatus {
  linked: boolean;
  githubLogin: string | null;
  tokenSuffix: string | null;
  scope: string | null;
  source: GithubTokenSource | null;
}

export interface GithubRepo {
  id: number;
  name: string;
  fullName: string;
  private: boolean;
  defaultBranch: string;
  updatedAt: string;
  description: string | null;
}

export function useGithubTokenStatus() {
  return useQuery({
    queryKey: githubKeys.token(),
    queryFn: async () => {
      const res = await getApi().v1.github.token.$get();
      return unwrap<GithubTokenStatus>(res);
    },
  });
}

export function useSaveGithubToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { token: string; githubLogin?: string; scope?: string }) => {
      const res = await getApi().v1.github.token.$put({ json: input });
      return unwrap<GithubTokenStatus>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: githubKeys.token() });
      void qc.invalidateQueries({ queryKey: githubKeys.repos() });
    },
  });
}

/**
 * Begins the GitHub OAuth flow:
 *   1. asks the API for the authorize URL (signed `state` is minted server-side),
 *   2. opens it with a full-page redirect.
 *
 * The callback always finishes on the SPA route (the former `mode` param and
 * Tauri deep-link flow were removed — 判定ボード D-1, 2026-07-05).
 * On success the user leaves the app; the token lands via the server-side
 * callback, and the SPA return re-validates the token query.
 */
export function useStartGithubOAuth() {
  return useMutation({
    mutationFn: async () => {
      const res = await getApi().v1.github.oauth.start.$get();
      const { url } = await unwrap<{ url: string }>(res);
      if (typeof window !== 'undefined') {
        window.location.href = url;
      }
      return { url };
    },
  });
}

export function useDeleteGithubToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await getApi().v1.github.token.$delete();
      return unwrap<{ deleted: boolean }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: githubKeys.token() });
      void qc.invalidateQueries({ queryKey: githubKeys.repos() });
    },
  });
}

/**
 * Lists the repositories the stored token can access. `enabled` is gated
 * by the caller (only fetch once the token is known to be linked) so a
 * missing token doesn't surface as a noisy 400.
 */
export function useGithubRepos(enabled: boolean) {
  return useQuery({
    queryKey: githubKeys.repos(),
    enabled,
    queryFn: async () => {
      const res = await getApi().v1.github.repos.$get();
      return unwrap<{ count: number; repos: GithubRepo[] }>(res);
    },
  });
}
